/**
 * Dryback unit SSOT — relative % of last-shot crest (P1/P2) or last-irrigation ref (overnight);
 * absolute VWC pts as display hints only.
 *
 * Field capacity / live VWC remain absolute VWC % on the sensor scale (e.g. FC = 58%).
 * P1/P2 dryback *bands* are relative of the last shot crest; overnight of last irrigation.
 * Day peak is not a CONTROL reference.
 *
 * Absolute 30% dryback on a 30% FC slab → 0% VWC (unsafe). Relative lands at 21%.
 *
 * Overnight: drop from last-P2 ref, scored as % of that ref
 * (matches controller overnightDrybackPercent in drybackPredictor.ts).
 */

export type DrybackRefName =
  | 'peak' // deprecated label — prefer 'last shot' / 'last irrigation'
  | 'FC'
  | 'saturation'
  | 'last P1 peak'
  | 'last P2'
  | 'last irrigation'
  | 'last shot'
  | 'overnight';

/** Live dryback as relative % of reference (crest / FC / last irrigation). ÷ ref. */
export function relativeDrybackPercent(ref: number, vwc: number): number {
  if (!(ref > 0) || !Number.isFinite(vwc)) return 0;
  return Math.max(0, ((ref - vwc) / ref) * 100);
}

/**
 * Observed overnight dryback as % of last P2 ref.
 * (lastP2 − morning) / lastP2 × 100
 */
export function overnightDrybackPercent(
  refVwc: number,
  endVwc: number,
): number | null {
  if (!(refVwc > 0) || !Number.isFinite(endVwc)) return null;
  return Math.round(((refVwc - endVwc) / refVwc) * 1000) / 10;
}

/** Convert a relative band % into absolute VWC points at the current reference (display hint). */
export function absolutePtsFromRelative(ref: number, relativePct: number): number {
  if (!(ref > 0) || !Number.isFinite(relativePct)) return 0;
  return (ref * relativePct) / 100;
}

/** Absolute VWC-point drop for overnight (ref − end), not ÷ peak. */
export function overnightPtsDrop(refVwc: number, endVwc: number): number {
  if (!Number.isFinite(refVwc) || !Number.isFinite(endVwc)) return 0;
  return Math.max(0, refVwc - endVwc);
}

/** VWC at which a relative dryback band is met. */
export function triggerVwcFromRelative(ref: number, relativePct: number): number {
  if (!(ref > 0) || !Number.isFinite(relativePct)) return 0;
  return ref * (1 - relativePct / 100);
}

/** Inverse: relative band % implied by an absolute trigger VWC vs ref. */
export function relativeFromTriggerVwc(ref: number, triggerVwc: number): number | null {
  if (!(ref > 0) || !Number.isFinite(triggerVwc)) return null;
  return Math.max(0, ((ref - triggerVwc) / ref) * 100);
}

/** Operator-facing label: relative primary + optional absolute pts hint. */
export function formatRelativeDryback(
  relativePct: number,
  ref?: number | null,
  opts?: { digits?: number; of?: DrybackRefName },
): string {
  const d = opts?.digits ?? 1;
  const of = opts?.of ?? 'peak';
  const pct = Number.isFinite(relativePct) ? relativePct : 0;
  const base = `${pct.toFixed(d)}% relative of ${of}`;
  if (ref != null && ref > 0 && Number.isFinite(ref)) {
    return `${base} (≈ ${absolutePtsFromRelative(ref, pct).toFixed(d)} pts)`;
  }
  return base;
}

/** Dual inline: "10% of peak (≈ 5.4 pts · ≤ 48.6% VWC)" */
export function formatDualDrybackInline(
  relativePct: number,
  ref?: number | null,
  opts?: { digits?: number; of?: DrybackRefName; includeTrigger?: boolean },
): string {
  const d = opts?.digits ?? 0;
  const of = opts?.of ?? 'peak';
  const pct = Number.isFinite(relativePct) ? relativePct : 0;
  const parts = [`${pct.toFixed(d)}% of ${of}`];
  if (ref != null && ref > 0 && Number.isFinite(ref)) {
    parts.push(`≈ ${absolutePtsFromRelative(ref, pct).toFixed(1)} pts`);
    if (opts?.includeTrigger !== false) {
      parts.push(`≤ ${triggerVwcFromRelative(ref, pct).toFixed(1)}% VWC`);
    }
  }
  if (parts.length === 1) return parts[0];
  return `${parts[0]} (${parts.slice(1).join(' · ')})`;
}

/** Dual trigger: "≤ 48.6% VWC (≈ 10% of peak)" */
export function formatDualTriggerInline(
  triggerVwc: number,
  bandPct?: number | null,
  opts?: { of?: DrybackRefName; digits?: number },
): string {
  const of = opts?.of ?? 'peak';
  const d = opts?.digits ?? 0;
  const base = `≤ ${triggerVwc.toFixed(1)}% VWC`;
  if (bandPct != null && Number.isFinite(bandPct) && bandPct > 0) {
    return `${base} (≈ ${bandPct.toFixed(d)}% of ${of})`;
  }
  return base;
}
